/* global React, Button, Icon, Modal, PlummeI18n, t */

const importPackageDateTime = (value) => value
  ? PlummeI18n.dateTime(value, { dateStyle: 'medium', timeStyle: 'short' })
  : t('set.import.packages.notAvailable');

const importPackageSize = (bytes) => {
  const size = Number(bytes || 0);
  if (!size) return t('set.import.packages.unknownSize');
  const units = ['B', 'KB', 'MB', 'GB'];
  const unitIndex = Math.min(Math.floor(Math.log(size) / Math.log(1024)), units.length - 1);
  const value = size / (1024 ** unitIndex);
  return `${PlummeI18n.number(value, { maximumFractionDigits: unitIndex > 1 ? 1 : 0 })} ${units[unitIndex]}`;
};

const importPackageStatus = (status) => ({
  imported: { label: t('set.import.packages.statusImported'), className: 'pp-paid' },
  duplicate: { label: t('set.import.packages.statusDuplicate'), className: 'pp-overdue' },
  previous: { label: t('set.import.packages.statusPrevious'), className: 'pp-muted' },
  analyzed: { label: t('set.import.packages.statusAnalyzed'), className: 'pp-sent' },
  failed: { label: t('set.import.packages.statusFailed'), className: 'pp-overdue' },
  analyzing: { label: t('set.import.packages.statusAnalyzing'), className: 'pp-muted' },
  uploaded: { label: t('set.import.packages.statusUploaded'), className: 'pp-muted' },
}[status] || { label: t('set.import.packages.statusUploaded'), className: 'pp-muted' });

const accountingImportProposalYears = (proposal = {}) => [...new Set([
  ...(proposal?.importYears || []),
  ...Object.keys(proposal?.openingBalanceByYear || {}),
  ...Object.entries(proposal?.accountingImportReportStatsByYear || {})
    .filter(([, stats]) => stats?.referenceOnly !== true
      && !(!stats?.accounts?.length && !stats?.monthly?.length && stats?.vat?.payments?.length))
    .map(([year]) => year),
  ...(proposal?.accountingImportLedgerRows || []).map((row) => row?.year),
].map((year) => String(year || '')).filter((year) => /^\d{4}$/.test(year)))].sort();

const recommendedAccountingImportYears = (years = [], currentYear = new Date().getFullYear(), country = 'NO') => {
  if (String(country || 'NO').toUpperCase() !== 'NO') return [...years];
  const minimumYear = currentYear - 5;
  return years.filter((year) => Number(year) >= minimumYear && Number(year) <= currentYear);
};

const accountingImportBankDefaults = (proposal, years) => {
  const year = [...years].map(String).sort()[0];
  return Object.fromEntries((proposal?.importedBankAccounts || []).map(account => {
    const suggestion = account.balanceSuggestionsByYear?.[year];
    return [account.id, { reconciledThrough: year ? `${year}-01-01` : '', startBalance: suggestion ? String(suggestion.balance) : '', confirmed: false }];
  }));
};

function AccountingImportYearSelector({ years = [], selectedYears = [], country = 'NO', onChange }) {
  if (!years.length) return null;
  const currentYear = new Date().getFullYear();
  const selected = new Set(selectedYears.map(String));
  const setYears = (next) => onChange?.([...new Set(next.map(String))].sort());
  const toggleYear = (year) => setYears(selected.has(year)
    ? selectedYears.filter((entry) => String(entry) !== year)
    : [...selectedYears, year]);
  const isNorway = String(country || 'NO').toUpperCase() === 'NO';
  const excludedRecommended = isNorway ? recommendedAccountingImportYears(years, currentYear, country).filter((year) => !selected.has(year)) : [];
  return <div style={{ padding: '12px 14px', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', background: 'var(--panel)' }}>
    <div className="row between gap-10" style={{ alignItems: 'flex-start', flexWrap: 'wrap' }}>
      <div><div className="t-sm" style={{ fontWeight: 900 }}>{t('set.import.years.title')}</div><div className="t-xs muted" style={{ marginTop: 3 }}>{t('set.import.years.help')}</div></div>
      <div className="row gap-6" style={{ flexWrap: 'wrap' }}>
        {isNorway && <Button variant="ghost" size="sm" onClick={() => setYears(recommendedAccountingImportYears(years, currentYear, country))} data-help={t('helpTopics.importYears.body')} data-help-title={t('helpTopics.importYears.title')}>{t('set.import.years.recommended')}</Button>}
        <Button variant="ghost" size="sm" onClick={() => setYears(years.filter((year) => Number(year) < currentYear))} data-help={t('helpTopics.importYears.body')} data-help-title={t('helpTopics.importYears.title')}>{t('set.import.years.previousOnly')}</Button>
        <Button variant="ghost" size="sm" onClick={() => setYears(years)} data-help={t('helpTopics.importYears.body')} data-help-title={t('helpTopics.importYears.title')}>{t('set.import.years.all')}</Button>
      </div>
    </div>
    <div className="row gap-8" style={{ flexWrap: 'wrap', marginTop: 12 }}>
      {years.map((year) => <label key={year} className="row gap-6" style={{ padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 'var(--r-sm)', background: 'var(--card)', cursor: 'pointer', fontWeight: 850 }}>
        <input type="checkbox" checked={selected.has(year)} onChange={() => toggleYear(year)}/><span>{year}{Number(year) === currentYear ? ` (${t('set.import.years.current')})` : ''}</span>
      </label>)}
    </div>
    {excludedRecommended.length > 0 && <div className="t-xs" style={{ marginTop: 10, color: 'var(--warning-ink)', fontWeight: 750 }}>{t('set.import.years.retentionWarning', { years: excludedRecommended.join(', ') })}</div>}
    <div className="t-xs muted" style={{ marginTop: 8 }}>{t('set.import.years.documentsPreserved')}</div>
  </div>;
}

function AccountingImportPackages({ packages = [], loading = false, deletingPath = '', downloadingPath = '', onDelete, onDownload }) {
  const [deleteTarget, setDeleteTarget] = React.useState(null);
  const confirmDelete = async () => {
    if (!deleteTarget || deletingPath) return;
    const deleted = await onDelete?.(deleteTarget);
    if (deleted !== false) setDeleteTarget(null);
  };
  const closeDeleteDialog = () => {
    if (!deletingPath) setDeleteTarget(null);
  };

  return <React.Fragment><div style={{ marginTop: 14, borderTop: '1px solid var(--border)', paddingTop: 14 }}>
    <div className="row between gap-12" style={{ alignItems: 'flex-start' }}>
      <div>
        <div className="t-sm" style={{ fontWeight: 900 }}>{t('set.import.packages.title')}</div>
        <div className="t-xs muted" style={{ marginTop: 3 }}>{t('set.import.packages.help')}</div>
      </div>
      {loading && <span className="t-xs muted">{t('set.import.packages.loading')}</span>}
    </div>
    {!loading && !packages.length && <div className="t-sm muted" style={{ marginTop: 10 }}>{t('set.import.packages.empty')}</div>}
    {packages.length > 0 && <div className="col gap-8" style={{ marginTop: 10 }}>
      {packages.map((item) => {
        const status = importPackageStatus(item.status);
        const busy = deletingPath === item.storagePath;
        return <div key={item.storagePath} style={{ border: '1px solid var(--border)', borderRadius: 'var(--r-ms)', padding: 12, background: 'var(--card)' }}>
          <div className="row between gap-12" style={{ alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <div style={{ minWidth: 0, flex: '1 1 280px' }}>
              <div className="row gap-8" style={{ flexWrap: 'wrap', alignItems: 'center' }}>
                <Icon name="fileText" size={17}/>
                <b className="t-sm" style={{ overflowWrap: 'anywhere' }}>{item.name}</b>
                <span className={`pp ${status.className}`}>{status.label}</span>
              </div>
              <div className="t-xs muted" style={{ marginTop: 6 }}>
                {t('set.import.packages.uploadedAt', { date: importPackageDateTime(item.uploadedAt) })} · {importPackageSize(item.size)}
              </div>
              {item.readAt && <div className="t-xs muted" style={{ marginTop: 3 }}>{t('set.import.packages.readAt', { date: importPackageDateTime(item.readAt) })}</div>}
              {item.importedAt && <div className="t-xs" style={{ marginTop: 3, fontWeight: 800 }}>{t('set.import.packages.importedAt', { date: importPackageDateTime(item.importedAt) })}</div>}
            </div>
            <div className="row gap-8" style={{ flexWrap: 'wrap' }}>
              <Button variant="secondary" size="sm" icon="download" disabled={Boolean(downloadingPath)} onClick={() => onDownload?.(item)}>
                {downloadingPath === item.storagePath ? t('set.import.packages.downloading') : t('set.import.packages.download')}
              </Button>
              <Button
                variant="danger"
                size="sm"
                icon="trash"
                disabled={!item.canDelete || Boolean(deletingPath)}
                title={!item.canDelete ? t('set.import.packages.deleteProtected') : undefined}
                onClick={() => setDeleteTarget(item)}
              >{busy ? t('set.import.packages.deleting') : t('set.import.packages.delete')}</Button>
            </div>
          </div>
          {!item.canDelete && <div className="t-xs muted" style={{ marginTop: 8 }}>{t('set.import.packages.protectedHelp')}</div>}
          {item.isDuplicate && <div className="t-xs muted" style={{ marginTop: 8 }}>{t('set.import.packages.duplicateHelp')}</div>}
        </div>;
      })}
    </div>}
  </div>
    <Modal
      open={Boolean(deleteTarget)}
      onClose={closeDeleteDialog}
      title={t('set.import.packages.deleteDialogTitle')}
      max={520}
      footer={<React.Fragment>
        <Button variant="ghost" disabled={Boolean(deletingPath)} onClick={closeDeleteDialog}>{t('common.cancel')}</Button>
        <Button variant="danger-solid" icon="trash" disabled={Boolean(deletingPath)} onClick={confirmDelete}>
          {deletingPath ? t('set.import.packages.deleting') : t('set.import.packages.delete')}
        </Button>
      </React.Fragment>}
    >
      <div className="col gap-12">
        <div className="row gap-10" style={{ alignItems: 'flex-start' }}>
          <span style={{ color: 'var(--error)', flex: 'none' }}><Icon name="alertTri" size={22}/></span>
          <div>
            <p className="t-body" style={{ margin: 0 }}>
              {t(deleteTarget?.importedAt ? 'set.import.packages.deletePreviousBody' : 'set.import.packages.deleteDialogBody', { name: deleteTarget?.name || 'ZIP' })}
            </p>
            <p className="t-sm" style={{ margin: '10px 0 0', fontWeight: 900, color: 'var(--error)' }}>{t('set.import.packages.deleteIrreversible')}</p>
          </div>
        </div>
      </div>
    </Modal>
  </React.Fragment>;
}

function AccountingImportImportedYears({ years = [], removingYear = '', onRemove }) {
  const [targetYear, setTargetYear] = React.useState('');
  const close = () => { if (!removingYear) setTargetYear(''); };
  const confirm = async () => {
    if (!targetYear || removingYear) return;
    const removed = await onRemove?.(targetYear);
    if (removed !== false) setTargetYear('');
  };
  if (!years.length) return null;
  return <React.Fragment>
    <div style={{ marginTop: 14, padding: 12, border: '1px solid var(--border)', borderRadius: 'var(--r-ms)', background: 'var(--panel)' }}>
      <div className="t-sm" style={{ fontWeight: 900 }}>{t('set.import.years.importedTitle')}</div>
      <div className="t-xs muted" style={{ marginTop: 3 }}>{t('set.import.years.importedHelp')}</div>
      <div className="row gap-8" style={{ marginTop: 10, flexWrap: 'wrap' }}>
        {years.map((year) => <Button key={year} variant="danger" size="sm" icon="trash" disabled={Boolean(removingYear)} onClick={() => setTargetYear(String(year))}>
          {removingYear === String(year) ? t('set.import.years.removing') : t('set.import.years.removeYear', { year })}
        </Button>)}
      </div>
    </div>
    <Modal open={Boolean(targetYear)} onClose={close} title={t('set.import.years.removeTitle', { year: targetYear })} max={540}
      footer={<React.Fragment><Button variant="ghost" disabled={Boolean(removingYear)} onClick={close}>{t('common.cancel')}</Button><Button variant="danger-solid" icon="trash" disabled={Boolean(removingYear)} onClick={confirm} data-help={t('helpTopics.importYears.body')} data-help-title={t('helpTopics.importYears.title')}>{removingYear ? t('set.import.years.removing') : t('set.import.years.removeConfirm')}</Button></React.Fragment>}>
      <div className="col gap-10">
        <p className="t-body" style={{ margin: 0 }}>{t('set.import.years.removeBody', { year: targetYear })}</p>
        <p className="t-sm muted" style={{ margin: 0 }}>{t('set.import.years.removePreserves')}</p>
        <p className="t-sm" style={{ margin: 0, fontWeight: 900, color: 'var(--error)' }}>{t('set.import.packages.deleteIrreversible')}</p>
      </div>
    </Modal>
  </React.Fragment>;
}

function AccountingImportReplaceDialog({ open, packages = [], onClose, onContinue }) {
  if (!open) return null;
  const activePackage = packages.find((item) => item.isActive) || packages[0];
  return <Modal
    open
    onClose={onClose}
    title={t('set.import.packages.replaceTitle')}
    max={540}
    footer={<React.Fragment>
      <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
      <Button icon="upload" onClick={onContinue} data-help={t('helpTopics.importRecheck.body')} data-help-title={t('helpTopics.importRecheck.title')}>{t('set.import.packages.chooseReplacement')}</Button>
    </React.Fragment>}
  >
    <div className="col gap-12">
      <div className="row gap-10" style={{ alignItems: 'flex-start' }}>
        <span style={{ color: 'var(--warning)', flex: 'none' }}><Icon name="alertTri" size={22}/></span>
        <div>
          <p className="t-body" style={{ margin: 0 }}>{t('set.import.packages.replaceBody', { name: activePackage?.name || 'ZIP' })}</p>
          <p className="t-sm muted" style={{ margin: '10px 0 0' }}>{t('set.import.packages.replaceSafety')}</p>
          <p className="t-sm" style={{ margin: '10px 0 0', fontWeight: 900 }}>{t('set.import.packages.replacePermanent')}</p>
        </div>
      </div>
    </div>
  </Modal>;
}

Object.assign(window, {
  AccountingImportPackages,
  AccountingImportReplaceDialog,
  AccountingImportImportedYears,
  AccountingImportYearSelector,
  accountingImportProposalYears,
  accountingImportBankDefaults,
  recommendedAccountingImportYears,
});
